import { useState, useEffect } from "react";

export default function AnalyticsTable({ token }) {
  const [data, setData] = useState(null);
  const [path, setPath] = useState([]);
  const [sortBy, setSortBy] = useState("members"); // "members" | "name" | "participated"
  const [sortDir, setSortDir] = useState("desc"); // "asc" | "desc"
  const [shareFormat, setShareFormat] = useState("list"); // "list" | "table"

  const fetchData = async (id = null, push = true) => {
    const url = id
      ? `https://smartcoreapi.ssfkerala.org/analytics/entity.php?entity_id=${id}`
      : `https://smartcoreapi.ssfkerala.org/analytics/summary.php`;

    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const json = await res.json();
    setData(json);

    if (id && push) {
      setPath(prev => [...prev, json.parent_entity]);
    } else if (!id) {
      setPath([]);
    }
  };

  useEffect(() => { fetchData(null, false); }, []);

  if (!data) return (
    <div className="bg-white rounded-xl shadow-sm p-8 text-center">
      <div className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  const currentChildren = data.sub_entities ?? data.entities ?? [];
  const hasSubEntities = currentChildren.length > 0;
  const hasParticipated = currentChildren.some(e => e.participated_sectors !== undefined);

  // Apply sorting
  const sortedChildren = [...currentChildren].sort((a, b) => {
    if (sortBy === "name") {
      const na = (a.orgname || "").toLowerCase();
      const nb = (b.orgname || "").toLowerCase();
      if (na < nb) return sortDir === "asc" ? -1 : 1;
      if (na > nb) return sortDir === "asc" ? 1 : -1;
      return 0;
    } else if (sortBy === "participated") {
      const pa = Number(a.participated_sectors || 0);
      const pb = Number(b.participated_sectors || 0);
      return sortDir === "asc" ? pa - pb : pb - pa;
    } else {
      // members
      const ma = Number(a.members_count || 0);
      const mb = Number(b.members_count || 0);
      return sortDir === "asc" ? ma - mb : mb - ma;
    }
  });

  const handleBack = () => {
    if (path.length === 0) return;
    const newPath = [...path];
    newPath.pop();
    setPath(newPath);
    const parentId = newPath.length > 0 ? newPath[newPath.length - 1].id : null;
    fetchData(parentId, false);
  };

  const handleShare = () => {
    const sorted = sortedChildren;

    let header = "Future summit Registration\nSmart core\n\n";

    // Level text (fix: use path.length + 1 to indicate current level)
    let levelText = "";
    if (path.length === 0) {
      levelText = "State Level Report\n";
    } else if (path.length === 1) {
      levelText = `District Level Report - ${data.parent_entity?.orgname || path[0].orgname}\n`;
    } else if (path.length === 2) {
      levelText = `Division Level Report - ${data.parent_entity?.orgname || path[1].orgname} (${path[0].orgname} District)\n`;
    } else {
      levelText = `${data.parent_entity?.orgname || path[path.length - 1].orgname} Report\n`;
    }

    // Body list
    let body = "";
    if (shareFormat === "list") {
      body = sorted.map((e, i) => {
        const participatedText = hasParticipated ? ` (${e.participated_sectors ?? 0} sectors)` : "";
        return `${i + 1}. ${e.orgname} - ${e.members_count}${participatedText}`;
      }).join("\n");
    } else {
      // table style: numbered rows with aligned columns (simple)
      const headers = hasParticipated ? ["#", "Name", "Members", "Participated"] : ["#", "Name", "Members"];
      const rows = [headers];
      sorted.forEach((e, i) => {
        const base = [String(i + 1), e.orgname || "-", String(e.members_count || 0)];
        if (hasParticipated) base.push(String(e.participated_sectors ?? 0));
        rows.push(base);
      });
      // convert to simple plain-text table
      const colWidths = rows[0].map((_, ci) => Math.max(...rows.map(r => (r[ci] || "").length)));
      body = rows.map(r => r.map((c, ci) => c.padEnd(colWidths[ci])).join(" | ")).join("\n");
    }

    // Summary
    const total = data.total_members ?? currentChildren.reduce((a, b) => a + (b.members_count || 0), 0);

    // Time
    const now = new Date();
    const timeString = now.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    const summary = `\n\nTotal Members: ${total}\nReport Generated: ${timeString}`;

    const text = `${header}${levelText}\n${body}${summary}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  // Export sorted children to CSV and trigger download
  const exportCSV = () => {
    const headers = hasParticipated ? ["#", "Name", "Members", "Participated Sectors"] : ["#", "Name", "Members"];
    const rows = sortedChildren.map((e, i) => {
      const base = [String(i + 1), e.orgname || "", String(e.members_count || 0)];
      if (hasParticipated) base.push(String(e.participated_sectors ?? 0));
      return base;
    });
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(path[path.length-1]?.orgname || "report").replace(/\s+/g,"_")}_report.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
  {path.length > 0 && (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 mb-3">
        {path.map((p, i) => (
          <span key={i} className="flex items-center gap-2">
            {i > 0 && <span>›</span>}
            <span className="font-medium">{p.orgname}</span>
          </span>
        ))}
      </div>
      <button
        onClick={handleBack}
        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition"
      >
        ← Back
      </button>
    </div>
  )}

  <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
    <div className="text-sm font-medium opacity-90 mb-1">Total Members</div>
    <div className="text-4xl font-bold">
      {data.total_members ?? currentChildren.reduce((a, b) => a + (b.members_count || 0), 0)}
    </div>
  </div>

  {hasSubEntities ? (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        {/* Sorting */}
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm text-gray-600">Sort by:</label>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="px-2 py-1 rounded border"
          >
            <option value="members">Members</option>
            <option value="name">Name</option>
            {hasParticipated && <option value="participated">Participated Sector</option>}
          </select>
          <button
            onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}
            className="px-2 py-1 bg-gray-100 rounded"
          >
            {sortDir === "asc" ? "↑" : "↓"}
          </button>
        </div>

        {/* Share & CSV */}
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm text-gray-600">Share format:</label>
          <select
            value={shareFormat}
            onChange={e => setShareFormat(e.target.value)}
            className="px-2 py-1 rounded border"
          >
            <option value="list">List</option>
            <option value="table">Table</option>
          </select>
          <button
            onClick={exportCSV}
            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Export CSV
          </button>
          <button
            onClick={handleShare}
            className="px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition"
          >
            📲 Share on WhatsApp
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-auto">
        <table className="w-full min-w-[300px]">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Members</th>
              {hasParticipated && (
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Participated Sector</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedChildren.map(e => (
              <tr
                key={e.id}
                onClick={() => fetchData(e.id)}
                className="cursor-pointer hover:bg-gray-50 transition"
              >
                <td className="px-4 py-4 text-sm font-medium text-gray-900">{e.orgname}</td>
                <td className="px-4 py-4 text-sm text-right">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                    {e.members_count}
                  </span>
                </td>
                {hasParticipated && (
                  <td className="px-4 py-4 text-sm text-right">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                      {e.participated_sectors ?? 0}
                    </span>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  ) : (
    <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">
      No sub-entities available
    </div>
  )}
</div>

  );
}
